/**
 * AxLabs Physics Experiments - Waves
 *
 * Collection of wave physics experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// STANDING WAVES ON STRING
// ============================================================================
export const StandingWavesString = createExperimentFromSchema({
    id: 'standing-waves-string',
    name: 'Standing Waves on a String',
    domain: 'physics',
    description: 'Standing wave patterns formed by interference of waves reflecting at fixed ends',
    conceptTags: ['Standing Waves', 'Harmonics', 'Nodes and Antinodes'],
    difficulty: 5,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'x1', name: 'Point 1 Position', unit: 'm', initial: 0.1, symbol: 'x_1' },
        { id: 'x2', name: 'Point 2 Position', unit: 'm', initial: 0.3, symbol: 'x_2' },
        { id: 'y1', name: 'Point 1 Displacement', unit: 'm', initial: 0, symbol: 'y_1' },
        { id: 'y2', name: 'Point 2 Displacement', unit: 'm', initial: 0, symbol: 'y_2' },
    ],
    parameters: [
        { id: 'L', name: 'String Length', unit: 'm', min: 0.5, max: 5, default: 1, symbol: 'L' },
        { id: 'T', name: 'Tension', unit: 'N', min: 1, max: 100, default: 20, symbol: 'T' },
        { id: 'mu', name: 'Linear Density', unit: 'kg/m', min: 0.001, max: 0.1, default: 0.01, symbol: '\\mu' },
        { id: 'A', name: 'Amplitude', unit: 'm', min: 0.01, max: 0.2, default: 0.05, symbol: 'A' },
        { id: 'n', name: 'Harmonic Number', unit: '', min: 1, max: 10, default: 3, symbol: 'n' },
    ],
    equations: [
        {
            id: 'wave-speed',
            name: 'Wave Speed',
            latex: 'v = \\sqrt{\\frac{T}{\\mu}}',
        },
        {
            id: 'fundamental',
            name: 'Fundamental Frequency',
            latex: 'f_1 = \\frac{v}{2L}',
        },
        {
            id: 'harmonic-freq',
            name: 'nth Harmonic Frequency',
            latex: 'f_n = \\frac{nv}{2L}',
        },
        {
            id: 'standing-wave',
            name: 'Standing Wave Pattern',
            latex: 'y(x,t) = 2A\\sin(\\frac{n\\pi x}{L})\\cos(\\omega_n t)',
        },
    ],
    derivatives: (_state, params, time) => {
        const v = Math.sqrt(getParam(params, 'T') / getParam(params, 'mu'));
        const omega = (getParam(params, 'n') * Math.PI * v) / getParam(params, 'L');
        const k = (getParam(params, 'n') * Math.PI) / getParam(params, 'L');
        const x1 = getState(_state, 'x1');
        const x2 = getState(_state, 'x2');
        const y1 = 2 * getParam(params, 'A') * Math.sin(k * x1) * Math.cos(omega * time);
        const y2 = 2 * getParam(params, 'A') * Math.sin(k * x2) * Math.cos(omega * time);
        return {
            t: 1,
            x1: 0,
            x2: 0,
            y1,
            y2,
        };
    },
    derivedState: (_state, params) => {
        const L = getParam(params, 'L');
        const T = getParam(params, 'T');
        const mu = getParam(params, 'mu');
        const n = getParam(params, 'n');
        const A = getParam(params, 'A');
        const v = Math.sqrt(T / mu);
        const f1 = v / (2 * L);
        const fn = n * f1;
        const omega = 2 * Math.PI * fn;
        const wavelength = 2 * L / n;
        // Count nodes and antinodes instead of returning arrays
        const node_count = n + 1;
        const antinode_count = n;
        return {
            wave_speed: v,
            fundamental_frequency: f1,
            harmonic_frequency: fn,
            angular_frequency: omega,
            wavelength,
            node_count,
            antinode_count,
            max_amplitude: 2 * A,
        };
    },
    energyComputation: (_state, params) => {
        const L = getParam(params, 'L');
        const T = getParam(params, 'T');
        const mu = getParam(params, 'mu');
        const n = getParam(params, 'n');
        const A = getParam(params, 'A');
        const t = getState(_state, 't');
        const v = Math.sqrt(T / mu);
        const omega = (n * Math.PI * v) / L;
        const energy = 0.25 * mu * L * A * A * omega * omega;
        return {
            kinetic: energy * Math.abs(Math.sin(omega * t)),
            potential: energy * Math.abs(Math.cos(omega * t)),
            thermal: 0,
            total: energy,
        };
    },
    bounds: {
        t: { min: 0, max: 100 },
        x1: { min: 0, max: 5 },
        x2: { min: 0, max: 5 },
    },
});
// ============================================================================
// SOUND WAVE INTERFERENCE (2 SOURCES)
// ============================================================================
export const SoundWaveInterference = createExperimentFromSchema({
    id: 'sound-wave-interference',
    name: 'Two-Source Sound Interference',
    domain: 'physics',
    description: 'Interference pattern from two coherent sound sources',
    conceptTags: ['Wave Interference', 'Coherence', 'Path Difference'],
    difficulty: 6,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'x', name: 'Observer X', unit: 'm', initial: 2, symbol: 'x' },
        { id: 'y', name: 'Observer Y', unit: 'm', initial: 0, symbol: 'y' },
        { id: 'pressure', name: 'Pressure Variation', unit: 'Pa', initial: 0, symbol: '\\Delta P' },
    ],
    parameters: [
        { id: 'f', name: 'Frequency', unit: 'Hz', min: 100, max: 2000, default: 440, symbol: 'f' },
        { id: 'd', name: 'Source Separation', unit: 'm', min: 0.1, max: 5, default: 1, symbol: 'd' },
        { id: 'A', name: 'Amplitude', unit: 'Pa', min: 0.1, max: 10, default: 1, symbol: 'A' },
        { id: 'v_sound', name: 'Speed of Sound', unit: 'm/s', min: 300, max: 400, default: 343, symbol: 'v_s' },
    ],
    equations: [
        {
            id: 'wave-1',
            name: 'Wave from Source 1',
            latex: '\\Delta P_1 = \\frac{A}{r_1}\\cos(kr_1 - \\omega t)',
        },
        {
            id: 'wave-2',
            name: 'Wave from Source 2',
            latex: '\\Delta P_2 = \\frac{A}{r_2}\\cos(kr_2 - \\omega t)',
        },
        {
            id: 'path-diff',
            name: 'Path Difference',
            latex: '\\Delta r = r_2 - r_1',
        },
    ],
    derivatives: (_state, params, time) => {
        const f = getParam(params, 'f');
        const d = getParam(params, 'd');
        const A = getParam(params, 'A');
        const v_sound = getParam(params, 'v_sound');
        const omega = 2 * Math.PI * f;
        const k = (2 * Math.PI * f) / v_sound;
        const x1 = -d / 2;
        const x2 = d / 2;
        const x = getState(_state, 'x');
        const y = getState(_state, 'y');
        const r1 = Math.sqrt(Math.pow(x - x1, 2) + y * y);
        const r2 = Math.sqrt(Math.pow(x - x2, 2) + y * y);
        const decay1 = r1 > 0.1 ? 1 / r1 : 10;
        const decay2 = r2 > 0.1 ? 1 / r2 : 10;
        const p1 = A * decay1 * Math.cos(k * r1 - omega * time);
        const p2 = A * decay2 * Math.cos(k * r2 - omega * time);
        return {
            t: 1,
            x: 0,
            y: 0,
            pressure: p1 + p2,
        };
    },
    derivedState: (state, params) => {
        const f = getParam(params, 'f');
        const d = getParam(params, 'd');
        const v_sound = getParam(params, 'v_sound');
        const x = getState(state, 'x');
        const y = getState(state, 'y');
        const wavelength = v_sound / f;
        const k = 2 * Math.PI / wavelength;
        const x1 = -d / 2;
        const x2 = d / 2;
        const r1 = Math.sqrt(Math.pow(x - x1, 2) + y * y);
        const r2 = Math.sqrt(Math.pow(x - x2, 2) + y * y);
        const pathDiff = r2 - r1;
        const phaseDiff = k * pathDiff;
        const n = Math.round(pathDiff / wavelength);
        const isConstructive = Math.abs(pathDiff - n * wavelength) < wavelength / 4;
        const isDestructive = Math.abs(pathDiff - (n + 0.5) * wavelength) < wavelength / 4;
        // Use numeric codes instead of strings
        // 0 = constructive, 1 = destructive, 2 = partial
        const interference_type = isConstructive ? 0 : isDestructive ? 1 : 2;
        return {
            wavelength,
            wave_number: k,
            path_difference: pathDiff,
            phase_difference: phaseDiff,
            interference_type,
            order_n: n,
        };
    },
    energyComputation: (state) => {
        const pressure = getState(state, 'pressure');
        const intensity = pressure * pressure / 343; // Approximation
        return { kinetic: intensity, potential: 0, thermal: 0, total: intensity };
    },
    bounds: {
        t: { min: 0, max: 10 },
        x: { min: -5, max: 5 },
        y: { min: -5, max: 5 },
    },
});
// ============================================================================
// DOPPLER EFFECT (MOVING SOURCE)
// ============================================================================
export const DopplerMovingSource = createExperimentFromSchema({
    id: 'doppler-moving-source',
    name: 'Doppler Effect - Moving Source',
    domain: 'physics',
    description: 'Frequency shift due to a sound source moving relative to stationary observer',
    conceptTags: ['Doppler Effect', 'Frequency Shift'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'x_source', name: 'Source Position', unit: 'm', initial: -5, symbol: 'x_s' },
        { id: 'x_observer', name: 'Observer Position', unit: 'm', initial: 5, symbol: 'x_o' },
        { id: 'frequency_observed', name: 'Observed Frequency', unit: 'Hz', initial: 0, symbol: 'f_{obs}' },
    ],
    parameters: [
        { id: 'f_source', name: 'Source Frequency', unit: 'Hz', min: 100, max: 1000, default: 440, symbol: 'f_s' },
        { id: 'v_source', name: 'Source Velocity', unit: 'm/s', min: -100, max: 100, default: 30, symbol: 'v_s' },
        { id: 'v_sound', name: 'Speed of Sound', unit: 'm/s', min: 300, max: 400, default: 343, symbol: 'v' },
    ],
    equations: [
        {
            id: 'doppler-source',
            name: 'Doppler Formula (Moving Source)',
            latex: 'f_{obs} = f_s\\left(\\frac{v}{v - v_s}\\right)',
        },
        {
            id: 'wavelength',
            name: 'Observed Wavelength',
            latex: '\\lambda_{obs} = \\frac{v - v_s}{f_s}',
        },
    ],
    derivatives: (_state, params) => {
        const v_source = getParam(params, 'v_source');
        return {
            t: 1,
            x_source: v_source,
            x_observer: 0,
            frequency_observed: 0,
        };
    },
    derivedState: (_state, params) => {
        const f_source = getParam(params, 'f_source');
        const v_source = getParam(params, 'v_source');
        const v_sound = getParam(params, 'v_sound');
        const mach = Math.abs(v_source) / v_sound;
        let f_observed = f_source;
        if (v_source !== 0) {
            // Approaching source increases frequency
            if (v_source > 0) {
                f_observed = f_source * (v_sound / (v_sound - v_source));
            }
            else {
                f_observed = f_source * (v_sound / (v_sound + v_source));
            }
        }
        return {
            observed_frequency: f_observed,
            frequency_shift: f_observed - f_source,
            wavelength_source: v_sound / f_source,
            wavelength_observed: (v_sound - v_source) / f_source,
            mach_number: mach,
            is_supersonic: mach >= 1 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'supersonic',
            name: 'Supersonic Source',
            check: (_state, params) => {
                const v_source = getParam(params, 'v_source');
                const v_sound = getParam(params, 'v_sound');
                return Math.abs(v_source) >= v_sound;
            },
            severity: 'warning',
            educationalMessage: 'The source is moving at or above the speed of sound. This creates a shock wave.',
        },
    ],
    bounds: {
        t: { min: 0, max: 30 },
        x_source: { min: -100, max: 100 },
        x_observer: { min: -50, max: 50 },
    },
});
// ============================================================================
// DOPPLER EFFECT (MOVING OBSERVER)
// ============================================================================
export const DopplerMovingObserver = createExperimentFromSchema({
    id: 'doppler-moving-observer',
    name: 'Doppler Effect - Moving Observer',
    domain: 'physics',
    description: 'Frequency shift due to an observer moving relative to stationary sound source',
    conceptTags: ['Doppler Effect', 'Reference Frames'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'x_source', name: 'Source Position', unit: 'm', initial: 0, symbol: 'x_s' },
        { id: 'x_observer', name: 'Observer Position', unit: 'm', initial: -5, symbol: 'x_o' },
        { id: 'frequency_observed', name: 'Observed Frequency', unit: 'Hz', initial: 0, symbol: 'f_{obs}' },
    ],
    parameters: [
        { id: 'f_source', name: 'Source Frequency', unit: 'Hz', min: 100, max: 1000, default: 440, symbol: 'f_s' },
        { id: 'v_observer', name: 'Observer Velocity', unit: 'm/s', min: -100, max: 100, default: 30, symbol: 'v_o' },
        { id: 'v_sound', name: 'Speed of Sound', unit: 'm/s', min: 300, max: 400, default: 343, symbol: 'v' },
    ],
    equations: [
        {
            id: 'doppler-observer',
            name: 'Doppler Formula (Moving Observer)',
            latex: 'f_{obs} = f_s\\left(\\frac{v + v_o}{v}\\right)',
        },
    ],
    derivatives: (_state, params) => {
        const v_observer = getParam(params, 'v_observer');
        return {
            t: 1,
            x_source: 0,
            x_observer: v_observer,
            frequency_observed: 0,
        };
    },
    derivedState: (_state, params) => {
        const f_source = getParam(params, 'f_source');
        const v_observer = getParam(params, 'v_observer');
        const v_sound = getParam(params, 'v_sound');
        let f_observed = f_source;
        if (v_observer !== 0) {
            // Approaching source increases frequency
            if (v_observer > 0) {
                f_observed = f_source * ((v_sound + v_observer) / v_sound);
            }
            else {
                f_observed = f_source * ((v_sound - Math.abs(v_observer)) / v_sound);
            }
        }
        return {
            observed_frequency: f_observed,
            frequency_shift: f_observed - f_source,
            mach_number: Math.abs(v_observer) / v_sound,
        };
    },
    failureModes: [
        {
            id: 'supersonic-observer',
            name: 'Supersonic Observer',
            check: (_state, params) => {
                const v_observer = getParam(params, 'v_observer');
                const v_sound = getParam(params, 'v_sound');
                return Math.abs(v_observer) >= v_sound;
            },
            severity: 'warning',
            educationalMessage: 'The observer is moving at or above the speed of sound.',
        },
    ],
    bounds: {
        t: { min: 0, max: 30 },
        x_source: { min: -50, max: 50 },
        x_observer: { min: -100, max: 100 },
    },
});
// ============================================================================
// BEAT FREQUENCY
// ============================================================================
export const BeatFrequency = createExperimentFromSchema({
    id: 'beat-frequency',
    name: 'Beat Frequency',
    domain: 'physics',
    description: 'Interference of two sound waves with slightly different frequencies creating beats',
    conceptTags: ['Beats', 'Frequency Difference', 'Amplitude Modulation'],
    difficulty: 3,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'displacement', name: 'Wave Displacement', unit: '', initial: 0, symbol: 'y' },
    ],
    parameters: [
        { id: 'f1', name: 'Frequency 1', unit: 'Hz', min: 200, max: 500, default: 440, symbol: 'f_1' },
        { id: 'f2', name: 'Frequency 2', unit: 'Hz', min: 200, max: 500, default: 445, symbol: 'f_2' },
        { id: 'A', name: 'Amplitude', unit: '', min: 0.1, max: 2, default: 1, symbol: 'A' },
    ],
    equations: [
        {
            id: 'beat-wave',
            name: 'Beat Wave',
            latex: 'y(t) = 2A\\cos(2\\pi\\frac{f_1-f_2}{2}t)\\cos(2\\pi\\frac{f_1+f_2}{2}t)',
        },
        {
            id: 'beat-freq',
            name: 'Beat Frequency',
            latex: 'f_{beat} = |f_1 - f_2|',
        },
    ],
    derivatives: (_state, params, time) => {
        const f1 = getParam(params, 'f1');
        const f2 = getParam(params, 'f2');
        const A = getParam(params, 'A');
        const omega1 = 2 * Math.PI * f1;
        const omega2 = 2 * Math.PI * f2;
        const y = A * Math.cos(omega1 * time) + A * Math.cos(omega2 * time);
        return {
            t: 1,
            displacement: y,
        };
    },
    derivedState: (state, params) => {
        const f1 = getParam(params, 'f1');
        const f2 = getParam(params, 'f2');
        const t = getState(state, 't');
        const f_beat = Math.abs(f1 - f2);
        const f_avg = (f1 + f2) / 2;
        const T_beat = f_beat > 0 ? 1 / f_beat : Infinity;
        const envelope = 2 * getParam(params, 'A') * Math.abs(Math.cos(Math.PI * (f1 - f2) * t));
        return {
            beat_frequency: f_beat,
            average_frequency: f_avg,
            beat_period: T_beat,
            envelope_amplitude: envelope,
            max_amplitude: 2 * getParam(params, 'A'),
            min_amplitude: 0,
        };
    },
    energyComputation: (state, params) => {
        const f1 = getParam(params, 'f1');
        const f2 = getParam(params, 'f2');
        const A = getParam(params, 'A');
        const t = getState(state, 't');
        const beatFreq = Math.abs(f1 - f2);
        const envelope = 2 * A * Math.abs(Math.cos(Math.PI * beatFreq * t));
        return {
            kinetic: envelope * envelope,
            potential: 0,
            thermal: 0,
            total: envelope * envelope,
        };
    },
    bounds: {
        t: { min: 0, max: 10 },
        displacement: { min: -2.5, max: 2.5 },
    },
});
// ============================================================================
// WAVE SUPERPOSITION
// ============================================================================
export const WaveSuperposition = createExperimentFromSchema({
    id: 'wave-superposition',
    name: 'Wave Superposition',
    domain: 'physics',
    description: 'General superposition of two traveling waves',
    conceptTags: ['Superposition Principle', 'Wave Addition'],
    difficulty: 4,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'x', name: 'Position', unit: 'm', initial: 0, symbol: 'x' },
        { id: 'y1', name: 'Wave 1 Amplitude', unit: 'm', initial: 0, symbol: 'y_1' },
        { id: 'y2', name: 'Wave 2 Amplitude', unit: 'm', initial: 0, symbol: 'y_2' },
        { id: 'y_total', name: 'Total Amplitude', unit: 'm', initial: 0, symbol: 'y_{total}' },
    ],
    parameters: [
        { id: 'A1', name: 'Wave 1 Amplitude', unit: 'm', min: 0, max: 2, default: 1, symbol: 'A_1' },
        { id: 'A2', name: 'Wave 2 Amplitude', unit: 'm', min: 0, max: 2, default: 1, symbol: 'A_2' },
        { id: 'f1', name: 'Wave 1 Frequency', unit: 'Hz', min: 0.5, max: 5, default: 1, symbol: 'f_1' },
        { id: 'f2', name: 'Wave 2 Frequency', unit: 'Hz', min: 0.5, max: 5, default: 1.5, symbol: 'f_2' },
        { id: 'k1', name: 'Wave 1 Wave Number', unit: 'rad/m', min: 0.5, max: 5, default: 2, symbol: 'k_1' },
        { id: 'k2', name: 'Wave 2 Wave Number', unit: 'rad/m', min: 0.5, max: 5, default: 2.5, symbol: 'k_2' },
        { id: 'phi1', name: 'Wave 1 Phase', unit: 'rad', min: 0, max: 2 * Math.PI, default: 0, symbol: '\\phi_1' },
        { id: 'phi2', name: 'Wave 2 Phase', unit: 'rad', min: 0, max: 2 * Math.PI, default: Math.PI / 4, symbol: '\\phi_2' },
        { id: 'v1', name: 'Wave 1 Speed', unit: 'm/s', min: -5, max: 5, default: 1, symbol: 'v_1' },
        { id: 'v2', name: 'Wave 2 Speed', unit: 'm/s', min: -5, max: 5, default: 1, symbol: 'v_2' },
    ],
    equations: [
        {
            id: 'wave1',
            name: 'Wave 1',
            latex: 'y_1(x,t) = A_1\\cos(k_1x - \\omega_1t + \\phi_1)',
        },
        {
            id: 'wave2',
            name: 'Wave 2',
            latex: 'y_2(x,t) = A_2\\cos(k_2x - \\omega_2t + \\phi_2)',
        },
        {
            id: 'superposition',
            name: 'Superposition',
            latex: 'y_{total} = y_1 + y_2',
        },
    ],
    derivatives: (_state, params, time) => {
        const A1 = getParam(params, 'A1');
        const A2 = getParam(params, 'A2');
        const f1 = getParam(params, 'f1');
        const f2 = getParam(params, 'f2');
        const k1 = getParam(params, 'k1');
        const k2 = getParam(params, 'k2');
        const phi1 = getParam(params, 'phi1');
        const phi2 = getParam(params, 'phi2');
        const x = getState(_state, 'x');
        const omega1 = 2 * Math.PI * f1;
        const omega2 = 2 * Math.PI * f2;
        const y1 = A1 * Math.cos(k1 * x - omega1 * time + phi1);
        const y2 = A2 * Math.cos(k2 * x - omega2 * time + phi2);
        const y_total = y1 + y2;
        return {
            t: 1,
            x: 0,
            y1,
            y2,
            y_total,
        };
    },
    derivedState: (state, params) => {
        const f1 = getParam(params, 'f1');
        const f2 = getParam(params, 'f2');
        const k1 = getParam(params, 'k1');
        const k2 = getParam(params, 'k2');
        const phi1 = getParam(params, 'phi1');
        const phi2 = getParam(params, 'phi2');
        const x = getState(state, 'x');
        const t = getState(state, 't');
        const A1 = getParam(params, 'A1');
        const A2 = getParam(params, 'A2');
        const omega1 = 2 * Math.PI * f1;
        const omega2 = 2 * Math.PI * f2;
        const v_p1 = omega1 / k1;
        const v_p2 = omega2 / k2;
        const phase1 = k1 * x - omega1 * t + phi1;
        const phase2 = k2 * x - omega2 * t + phi2;
        const phaseDiff = phase2 - phase1;
        const normalizedPhase = ((phaseDiff % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const isConstructive = normalizedPhase < 0.5 || normalizedPhase > 2 * Math.PI - 0.5;
        const isDestructive = Math.abs(normalizedPhase - Math.PI) < 0.5;
        // Use numeric codes: 0 = constructive, 1 = destructive, 2 = intermediate
        const interference_type = isConstructive ? 0 : isDestructive ? 1 : 2;
        return {
            phase_velocity_1: v_p1,
            phase_velocity_2: v_p2,
            phase_difference: phaseDiff,
            interference_type,
            max_possible_amplitude: A1 + A2,
            min_possible_amplitude: Math.abs(A1 - A2),
        };
    },
    energyComputation: (state) => {
        const y1 = getState(state, 'y1');
        const y2 = getState(state, 'y2');
        const y_total = getState(state, 'y_total');
        const ke = y1 * y1 + y2 * y2;
        const pe = y_total * y_total * 0.5;
        return {
            kinetic: ke,
            potential: pe,
            thermal: 0,
            total: ke + pe,
        };
    },
    bounds: {
        t: { min: 0, max: 20 },
        x: { min: -10, max: 10 },
        y_total: { min: -4, max: 4 },
    },
});
//# sourceMappingURL=waves.js.map